import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, PlayCircle, Plus, Settings2, Trash2 } from "lucide-react";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDate, offLabel } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { BikeStatus } from "@/components/fleet/BikeCards";
import { BikeQR } from "@/components/fleet/BikeQR";
import { KINDS, RecordDialog } from "@/components/fleet/RecordDialog";
import { ComplianceDialog } from "@/components/fleet/ComplianceDialog";
import { FaultHistory } from "@/components/fleet/FaultHistory";
import { Avatar, AvatarEdit } from "@/components/Avatar";

const STATE = { ok: "border-emerald-500/40 text-emerald-300", due_soon: "border-yellow-400/50 text-yellow-300", overdue: "border-red-500/50 bg-red-500/10 text-red-300", missing: "border-zinc-500/40 text-zinc-400" };

const Compliance = ({ items }) => (
  <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="bike-compliance">
    {items.map((c) => (
      <div key={c.item} className={`rounded-xl border-2 p-3 ${STATE[c.state]}`}>
        <div className="text-[11px] font-semibold uppercase tracking-widest">{c.item}</div>
        <div className="mt-1 font-mono text-sm">{c.due ? (c.due.includes("mi") ? c.due : fmtDate(c.due)) : "Not set"}</div>
        <div className="text-[11px]">{{ ok: "OK", due_soon: "Due soon", overdue: "Overdue / expired", missing: "Not recorded" }[c.state]}</div>
      </div>
    ))}
  </div>
);

const Records = ({ records, kind, setKind, canManage, onDelete }) => (
  <section className="overflow-hidden rounded-2xl border bg-card" data-testid="maintenance-records">
    <div className="flex gap-2 overflow-x-auto border-b px-4 py-3">
      {["", ...Object.keys(KINDS)].map((k) => (
        <button key={k || "all"} onClick={() => setKind(k)} data-testid={`record-filter-${k || "all"}`} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium ${kind === k ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{k ? KINDS[k] : "All"}</button>
      ))}
    </div>
    {records.filter((r) => !kind || r.kind === kind).length === 0 && <p className="px-5 py-8 text-sm text-muted-foreground">No records.</p>}
    {records.filter((r) => !kind || r.kind === kind).map((r) => (
      <div key={r.id} data-testid={`record-row-${r.id}`} className="space-y-1 border-b px-5 py-4 last:border-0">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="font-semibold"><span className="mr-2 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">{KINDS[r.kind]}</span>{r.title}</div>
            <div className="text-xs text-muted-foreground">{fmtDate(r.date)}{r.mileage != null ? ` · ${r.mileage} mi` : ""}{r.supplier ? ` · ${r.supplier}` : ""}{r.cost != null ? ` · £${Number(r.cost).toFixed(2)}` : ""}{r.invoice_ref ? ` · Inv ${r.invoice_ref}` : ""}{r.position ? ` · ${{ front: "Front", rear: "Rear", both: "Both" }[r.position] || r.position}` : ""}{r.result ? ` · ${{ pass: "Pass", advisory: "Pass with advisories", fail: "Fail" }[r.result] || r.result}` : ""}{r.policy_number ? ` · Policy ${r.policy_number}` : ""}{r.next_due_date ? ` · Next due ${fmtDate(r.next_due_date)}` : ""}</div>
            {r.details && <p className="text-sm text-muted-foreground">{r.details}</p>}
          </div>
          {canManage && <button onClick={() => onDelete(r)} data-testid={`record-delete-${r.id}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
        </div>
        <div className="flex flex-wrap gap-2">
          {r.files.map((f, i) => (
            <button key={i} onClick={() => downloadFile(`/maintenance/${r.id}/files/${i}`, f.filename).catch((e) => toast.error(errMsg(e)))} data-testid={`record-file-${r.id}-${i}`} className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:bg-secondary"><FileDown className="h-3 w-3" /> {f.filename}</button>
          ))}
        </div>
      </div>
    ))}
  </section>
);

export default function BikeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [bike, setBike] = useState(null);
  const [records, setRecords] = useState([]);
  const [faults, setFaults] = useState([]);
  const [kind, setKind] = useState("");
  const [missing, setMissing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const isViewer = ["admin", "fleet_manager", "trustee"].includes(user.role);
  const canManage = ["admin", "fleet_manager"].includes(user.role);
  const canRun = ["admin", "assessor", "rider"].includes(user.role);

  const load = () => api.get(`/bikes/${id}`).then((r) => setBike(r.data)).catch(() => setMissing(true));
  useEffect(() => {
    load();
    if (isViewer) api.get("/maintenance", { params: { bike_id: id } }).then((r) => setRecords(r.data));
    if (isViewer) api.get("/faults", { params: { bike_id: id } }).then((r) => setFaults(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (r) => {
    if (!window.confirm(`Delete record "${r.title}"?`)) return;
    await api.delete(`/maintenance/${r.id}`);
    setRecords((x) => x.filter((y) => y.id !== r.id));
  };

  if (missing) return <p data-testid="bike-not-found" className="text-sm text-muted-foreground">Bike not found.</p>;
  if (!bike) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  return (
    <div className="space-y-6">
      <Link to="/fleet" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Fleet</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {["admin", "fleet_manager"].includes(user.role) ? <AvatarEdit photo={bike.photo} name={bike.name} square endpoint={`/avatars/bike/${bike.id}`} onSaved={(p) => setBike({ ...bike, photo: p })} testid="bike-avatar" /> : <Avatar photo={bike.photo} name={bike.name} size="lg" square testid="bike-avatar" />}
          <div>
          <h1 data-testid="bike-detail-name" className="text-3xl font-extrabold tracking-tight sm:text-4xl">{bike.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground"><span className="font-mono">{bike.registration || "Reg not set"}</span>{bike.make_model && <span>{bike.make_model}</span>}<BikeStatus bike={bike} /><span>{bike.last_mileage ?? "—"} mi</span></div>
          {bike.status === "off_road" && <p className="mt-2 text-sm text-red-300">{offLabel(bike)}: {bike.off_road_reason}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canRun && bike.status !== "off_road" && !bike.current_run_id && <button onClick={() => nav(`/bike-check?bike=${bike.id}`)} data-testid="bike-start-check-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"><PlayCircle className="h-4 w-4" /> Start pre-ride check</button>}
          {canManage && <button onClick={() => setAdding(true)} data-testid="add-record-btn" className="inline-flex items-center gap-2 rounded-full bg-destructive px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700"><Plus className="h-4 w-4" /> Add record</button>}
          {canManage && <button onClick={() => setEditing(true)} data-testid="edit-compliance-btn" className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold hover:bg-secondary"><Settings2 className="h-4 w-4" /> Compliance dates</button>}
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-12 [&>*]:min-w-0">
        <div className="min-w-0 space-y-6 lg:col-span-9">
          {isViewer && bike.compliance && <Compliance items={bike.compliance} />}
          {isViewer && bike.insurance_insurer && <p className="text-sm text-muted-foreground">Insurance: {bike.insurance_insurer} · Policy {bike.insurance_policy || "—"} · Tax status {(bike.tax_status || "—").toUpperCase()}</p>}
          {isViewer && <FaultHistory faults={faults} records={records} />}
          {isViewer && <Records records={records} kind={kind} setKind={setKind} canManage={canManage} onDelete={remove} />}
        </div>
        <div className="lg:col-span-3"><BikeQR bike={bike} /></div>
      </div>
      {adding && <RecordDialog bike={bike} onClose={() => setAdding(false)} onSaved={(r) => { setRecords((x) => [r, ...x]); load(); }} />}
      {editing && <ComplianceDialog bike={bike} onClose={() => setEditing(false)} onSaved={setBike} />}
    </div>
  );
}
